/**
 * Personal Details Screen for Ramps Flow
 *
 * Dedicated screen for entering personal name information
 * Part of the sequential flow: Calculator → Name → Phone → Address → ID → Review → Status
 */

import { t } from "@lingui/core/macro";
import { createFileRoute } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { useEffect, useState } from "react";
import { RampsButton } from "@/components/ramps/Button";
import { LegalNameConfirmationDrawer } from "@/components/ramps/LegalNameConfirmationDrawer";
import { useContinue } from "@/hooks/useContinue";
import { useNavigateBack } from "@/hooks/useNavigateBack";
import { useAnalytics } from "@/hooks/usePostHog";
import { RampsInput } from "../components/ramps/Input";
import { RampsText } from "../components/ramps/Text";
import { Progress } from "../components/ui/progress";
import { nameStepSchema, useRampsForm } from "../hooks/useRampsForm";
import { rampsActions, useRampsStore } from "../stores/use-ramps-store";
import {
	FIRST_NAME_FIELD_MAX,
	MIDDLE_NAME_FIELD_MAX,
	NAME_FIELD_MAX,
	sanitizeNameInput,
} from "../utils/senderNames";

export const Route = createFileRoute("/ramps/name")({
	validateSearch: (search) =>
		search as {
			step?: string;
			transaction_id?: string;
			token?: string;
			accesstoken?: string;
		},
	component: PersonalDetailsPage,
});

function PersonalDetailsPage() {
	const search = Route.useSearch(); // Get current search params
	const store = useRampsStore();
	const form = useRampsForm();
	const [showConfirmationDrawer, setShowConfirmationDrawer] = useState(false);
	const handleContinue = useContinue();
	const handleBack = useNavigateBack();
	const { trackPageView } = useAnalytics();

	// Track page view
	useEffect(() => {
		trackPageView("name", { page_name: "name" });
	}, [trackPageView]);

	// Check if profile data comes from backend (existing user profile)
	const isExistingProfile = store.quoteResponse?.isExistingProfile ?? false;

	// Token and transaction_id are synced globally by URLParamsInitializer

	const handleSubmit = () => {
		// Validate current step before proceeding
		const stepValidation = nameStepSchema.safeParse(form.state.values);

		if (stepValidation.success) {
			// Show confirmation drawer before proceeding
			setShowConfirmationDrawer(true);
		}
	};

	const handleConfirmName = () => {
		// Save name data to store first
		const { firstName, lastName, middleName, secondLastName } =
			form.state.values;
		rampsActions.setPersonalInfo({
			firstName,
			lastName,
			middleName,
			secondLastName,
		});

		// Mark name as confirmed and proceed to address
		rampsActions.confirmLegalName(true);
		rampsActions.setCurrentStep("address");
		handleContinue(search);
	};

	const handleEditName = () => {
		// Close drawer and let user edit
		setShowConfirmationDrawer(false);
		rampsActions.confirmLegalName(false);
	};

	// Helper function to get first error message from field, falling back to
	// a max-length reached hint (the hard `maxLength` cap on the input means a
	// Zod "too long" error can never actually fire under normal typing/paste).
	const getFieldError = (
		fieldName: keyof typeof nameStepSchema.shape,
		value: string,
		maxLength: number,
	) => {
		const field = form.getFieldMeta(fieldName);
		if (field?.errors && field.errors.length > 0) {
			const error = field.errors[0];
			return typeof error === "string" ? error : error.message || String(error);
		}
		if (value.length >= maxLength) {
			return t`Maximum ${maxLength} characters reached`;
		}
		return undefined;
	};

	// Helper function to capitalize first letter of each word
	const capitalizeFirstLetter = (value: string) => {
		return value
			.split(" ")
			.map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
			.join(" ");
	};

	// Combined helper for name fields: strip API-disallowed characters
	// (digits, punctuation, symbols) then capitalize each word.
	const formatNameInput = (value: string) => {
		return capitalizeFirstLetter(sanitizeNameInput(value));
	};

	// Format display name for confirmation display
	const getDisplayName = () => {
		const { firstName, lastName, middleName, secondLastName } =
			form.state.values;
		const parts = [
			capitalizeFirstLetter(firstName),
			capitalizeFirstLetter(middleName),
			capitalizeFirstLetter(lastName),
			capitalizeFirstLetter(secondLastName),
		].filter(Boolean);
		return parts.join(" ");
	};

	return (
		<div className="flex min-h-dvh flex-col bg-ramps-background-base">
			{/* Header */}
			<div className="flex items-center justify-between p-4">
				<button
					type="button"
					onClick={handleBack}
					className="flex h-10 w-10 items-center justify-center rounded-full text-ramps-text-primary transition-colors hover:bg-ramps-background-surface-3"
				>
					<ArrowLeft className="h-5 w-5" />
				</button>
			</div>

			{/* Progress bar */}
			<div className="mb-6 px-4">
				<Progress value={35} className="h-2" />
				<div className="mt-2 flex justify-between">
					<RampsText variant="title-lg-bold" color="primary">
						{t`Your name`}
					</RampsText>
				</div>
			</div>

			{/* Content */}
			<div className="flex flex-1 flex-col px-4 pb-4">
				<div className="grid grid-cols-1 gap-3">
					<form.Field
						name="firstName"
						validators={{
							onChange: nameStepSchema.shape.firstName,
						}}
					>
						{(field) => (
							<RampsInput
								label={t`First Name`}
								type="text"
								maxLength={FIRST_NAME_FIELD_MAX}
								value={field.state.value}
								onChange={(e) =>
									field.handleChange(formatNameInput(e.target.value))
								}
								onBlur={field.handleBlur}
								error={getFieldError(
									"firstName",
									field.state.value,
									FIRST_NAME_FIELD_MAX,
								)}
								disabled={isExistingProfile}
							/>
						)}
					</form.Field>
					<form.Field
						name="middleName"
						validators={{
							onChange: nameStepSchema.shape.middleName,
						}}
					>
						{(field) => (
							<RampsInput
								label={t`Middle Name (Optional)`}
								type="text"
								maxLength={MIDDLE_NAME_FIELD_MAX}
								value={field.state.value}
								onChange={(e) =>
									field.handleChange(formatNameInput(e.target.value))
								}
								onBlur={field.handleBlur}
								error={getFieldError(
									"middleName",
									field.state.value ?? "",
									MIDDLE_NAME_FIELD_MAX,
								)}
								disabled={isExistingProfile}
							/>
						)}
					</form.Field>
					<form.Field
						name="lastName"
						validators={{
							onChange: nameStepSchema.shape.lastName,
						}}
					>
						{(field) => (
							<RampsInput
								label={t`Last Name`}
								type="text"
								maxLength={NAME_FIELD_MAX}
								value={field.state.value}
								onChange={(e) =>
									field.handleChange(formatNameInput(e.target.value))
								}
								onBlur={field.handleBlur}
								error={getFieldError(
									"lastName",
									field.state.value,
									NAME_FIELD_MAX,
								)}
								disabled={isExistingProfile}
							/>
						)}
					</form.Field>
					<form.Field
						name="secondLastName"
						validators={{
							onChange: nameStepSchema.shape.secondLastName,
						}}
					>
						{(field) => (
							<RampsInput
								label={t`Second Last Name (Optional)`}
								type="text"
								maxLength={NAME_FIELD_MAX}
								value={field.state.value}
								onChange={(e) =>
									field.handleChange(formatNameInput(e.target.value))
								}
								onBlur={field.handleBlur}
								error={getFieldError(
									"secondLastName",
									field.state.value ?? "",
									NAME_FIELD_MAX,
								)}
								disabled={isExistingProfile}
							/>
						)}
					</form.Field>
				</div>
				{/* Continue Button */}
				<div className="mt-auto justify-center align-bottom">
					<form.Subscribe
						selector={(state) => nameStepSchema.safeParse(state.values).success}
					>
						{(isStepValid) => (
							<RampsButton
								onClick={handleSubmit}
								disabled={!isStepValid}
								className="h-12 w-full rounded-lg bg-black font-medium text-white hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-50"
							>
								{t`Continue`}
							</RampsButton>
						)}
					</form.Subscribe>
				</div>
			</div>

			{/* Legal Name Confirmation Drawer */}
			<LegalNameConfirmationDrawer
				open={showConfirmationDrawer}
				onOpenChange={setShowConfirmationDrawer}
				name={getDisplayName()}
				onConfirm={handleConfirmName}
				onEdit={handleEditName}
			/>
		</div>
	);
}
